import { NOOP } from '@lemonade-hq/ts-helpers';
import { createContext, lazy, Suspense, useCallback, useContext, useLayoutEffect, useMemo, useState } from 'react';
import type { PropsWithChildren } from 'react';
import { COLORS_VERSION } from '../assetProcessing/transformSvg';
import { ToastProvider } from '../components/Toast/Toast';
import { bluishBrandClass, reddishThemeClass } from '../styles/colors.css';
import { darkThemeClass } from '../styles/theme.css';
import { AsyncDataProvider } from './AsyncDataProvider';
import { ContextMenuProvider } from './ContextMenuProvider';
import { ModalProvider } from './ModalProvider';
import { ALLOW_THEME_TOKEN, isAllowedToTransform } from './themeConstants';
import type { Brand, Scheme } from './themeConstants';

export { NO_THEME_TOKEN, ALLOW_THEME_TOKEN, type Brand, type Scheme } from './themeConstants';

const LazyIconSprite = lazy(
  async () => await import('../components/Icon/IconSprite').then(module => ({ default: module.IconSprite })),
);

export interface Theme {
  readonly brand: Brand;
  readonly scheme: Scheme;
}

interface ThemeContextValue {
  readonly theme: Theme;
  readonly setTheme: (theme: Partial<Theme>) => void;
  readonly getThemeImageUrl: (imageUrl: string) => string;
}

const ThemeContext = createContext<ThemeContextValue>({
  theme: { brand: 'pinkish', scheme: 'light' },
  setTheme: NOOP,
  getThemeImageUrl: /* istanbul ignore next */ () => '',
});

export const ThemeProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [theme, setTheme] = useState<Theme>({ brand: 'pinkish', scheme: 'light' });

  useLayoutEffect(() => {
    document.body.classList.toggle(reddishThemeClass, theme.brand === 'reddish');
    document.body.classList.toggle(bluishBrandClass, theme.brand === 'bluish');
    document.body.classList.toggle(darkThemeClass, theme.scheme === 'dark');
  }, [theme]);

  const getThemeImageUrl = useCallback(
    (imageUrl: string) => {
      if (!imageUrl.includes(ALLOW_THEME_TOKEN) || !isAllowedToTransform(imageUrl)) {
        return imageUrl;
      }

      const themePattern = /_theme=[^.]+/;

      if (theme.brand === 'pinkish' && theme.scheme === 'light') {
        return imageUrl.replace(themePattern, '');
      }

      const themeSuffix = `_theme=${theme.scheme}_${theme.brand}_${COLORS_VERSION}`;

      if (themePattern.test(imageUrl)) {
        return imageUrl.replace(themePattern, themeSuffix);
      }

      return imageUrl.replace(/\.svg$/, `${themeSuffix}.svg`);
    },
    [theme.brand, theme.scheme],
  );

  const updateTheme = useCallback(
    (newTheme: Partial<Theme>) => {
      setTheme(prevTheme => ({
        brand: newTheme.brand ?? prevTheme.brand,
        scheme: newTheme.scheme ?? prevTheme.scheme,
      }));
    },
    [setTheme],
  );

  const contextValue = useMemo(
    () => ({ theme, setTheme: updateTheme, getThemeImageUrl }),
    [theme, getThemeImageUrl, updateTheme],
  );

  return (
    <ThemeContext.Provider value={contextValue}>
      <ContextMenuProvider>
        <ToastProvider>
          <ModalProvider>
            <AsyncDataProvider>
              <Suspense fallback={null}>
                <LazyIconSprite />
              </Suspense>
              {children}
            </AsyncDataProvider>
          </ModalProvider>
        </ToastProvider>
      </ContextMenuProvider>
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  return useContext(ThemeContext);
};

export const useThemeImage = (imageUrl: string) => {
  const { getThemeImageUrl } = useTheme();

  return useMemo(() => getThemeImageUrl(imageUrl), [getThemeImageUrl, imageUrl]);
};
